import { useEffect, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  createGoLink,
  deleteGoLink,
  isPrintedGoSlug,
  isValidGoSlug,
  updateGoLink,
  type GoLink,
} from '../../lib/goLinks';
import ConfirmModal from '../Common/ConfirmModal';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

export default function GoLinkModal({
  isOpen,
  onClose,
  link,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  link: GoLink | null;
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [destination, setDestination] = useState('');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const printed = Boolean(link && isPrintedGoSlug(link.slug));
  const inCatalog = link?.inCatalog !== false;

  useEffect(() => {
    if (!isOpen) return;
    setSlug(link?.slug ?? '');
    setDestination(link?.destination_url ?? 'https://');
    setLabel(link?.label ?? '');
    setNotes(link?.notes ?? '');
    setConfirmDeleteOpen(false);
    setDeleting(false);
  }, [isOpen, link]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDeleteOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, confirmDeleteOpen]);

  const handleSave = async () => {
    const normalizedSlug = slug.trim().toLowerCase();
    if (!isValidGoSlug(normalizedSlug)) {
      toast.error('Slug: start with a letter or number, then lowercase letters, digits, or hyphens');
      return;
    }
    let destinationUrl = destination.trim();
    try {
      const parsed = new URL(destinationUrl);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new Error('https only');
      }
      destinationUrl = parsed.toString();
    } catch {
      toast.error('Destination must be a full http(s) URL');
      return;
    }

    setSaving(true);
    try {
      if (link && inCatalog) {
        await updateGoLink(link.slug, {
          destination_url: destinationUrl,
          label: label.trim() || null,
          notes: notes.trim() || null,
        });
        toast.success(`/${link.slug} updated — live within ~15s`);
      } else {
        await createGoLink({
          slug: normalizedSlug,
          destination_url: destinationUrl,
          label: label.trim() || null,
          notes: notes.trim() || null,
        });
        toast.success(`/${normalizedSlug} added`);
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!link) return;
    setDeleting(true);
    try {
      const result = await deleteGoLink(link.slug);
      toast.success(
        result.kind === 'catalog'
          ? `/${link.slug} deleted — live within ~15s`
          : `/${link.slug} dismissed — crawler clicks removed`,
      );
      setConfirmDeleteOpen(false);
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {link ? (inCatalog ? `Edit /${link.slug}` : `Dismiss /${link.slug}`) : 'New go link'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug</label>
            <input
              className={inputClass}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={Boolean(link)}
              placeholder="card"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {link
                ? 'Slug is the printed URL. Change the destination, not the slug.'
                : 'Becomes go.stephansmith.me/this'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lands on</label>
            <input
              className={inputClass}
              type="url"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="https://stephansmith.me/microconf"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
            <input
              className={inputClass}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Business card"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              className={inputClass}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Printed QR — do not rename"
            />
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            {link ? (
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={saving || deleting || printed}
                title={printed ? 'Printed QR — retarget instead of deleting' : `Delete /${link.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || deleting}
              className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {link ? (
        <ConfirmModal
          isOpen={confirmDeleteOpen}
          title={`Delete /${link.slug}?`}
          description={
            inCatalog ? (
              <>
                <p>
                  <code>go.stephansmith.me/{link.slug}</code> will 404. Click rows for this slug are
                  removed too.
                </p>
                <p className="mt-2">This cannot be undone.</p>
              </>
            ) : (
              <>
                <p>
                  <code>/{link.slug}</code> was never a go link — only crawler clicks. Delete removes
                  those click rows.
                </p>
                <p className="mt-2">This cannot be undone.</p>
              </>
            )
          }
          confirmLabel={deleting ? 'Deleting…' : 'Delete'}
          variant="danger"
          busy={deleting}
          onConfirm={() => void handleDelete()}
          onClose={() => {
            if (!deleting) setConfirmDeleteOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
